/**
 * Environment Context
 *
 * Provides server-detected environment value to client components.
 * This prevents hydration mismatches by ensuring both server and client
 * use the same environment for domain URL generation.
 */

import { createContext, useContext } from "react";
import type { Environment } from "~/types/domain";

interface EnvironmentContextValue {
  environment: Environment;
}

const EnvironmentContext = createContext<EnvironmentContextValue | null>(null);

interface EnvironmentProviderProps {
  environment: Environment;
  children: React.ReactNode;
}

/**
 * Provider component that supplies the environment value to all child components.
 * Should be placed at the root of the application.
 */
export function EnvironmentProvider({
  environment,
  children,
}: EnvironmentProviderProps) {
  return (
    <EnvironmentContext.Provider value={{ environment }}>
      {children}
    </EnvironmentContext.Provider>
  );
}

/**
 * Hook to access the current environment from context.
 *
 * Returns the environment set by the root loader, or undefined if:
 * - Called outside of EnvironmentProvider
 * - Provider has not received environment from loader yet
 *
 * Components using this hook should handle the undefined case gracefully
 * by falling back to window-based detection (for backwards compatibility).
 */
export function useEnvironment(): Environment {
  const context = useContext(EnvironmentContext);
  return context?.environment;
}
